import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ProgressBar, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWarehouse } from '@fortawesome/free-solid-svg-icons';

import useAdvanceTable from '../../hooks/useAdvanceTable';
import AdvanceTableProvider from '../../providers/AdvanceTableProvider';
import AdvanceTable from '../../components/base/AdvanceTable';
import AdvanceTableFooter from '../../components/base/AdvanceTableFooter';
import Badge from '../../components/base/Badge';

import FilterTab from '../../components/common/FilterTab';
import SearchBox from '../../components/common/SearchBox';
import FilterButtonGroup from '../../components/common/FilterButtonGroup';
import ReserveFromStockModal from '../../components/sales-management/ReserveFromStockModal';
import TableExpandButton from '../../components/common/TableExpandButton';
import { FULL_TABLE_ROUTES, FULL_TABLE_PAGE_SIZE } from '../full-table/routes';

const safeArr = v => (Array.isArray(v) ? v : []);
const fmt = n => Number(n || 0).toLocaleString();
const num = v => (Number.isFinite(Number(v)) ? Number(v) : 0);

const formatDate = iso => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

const normalizeDateKey = iso => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  // compare by local “day” is fine here
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
};

function buildLineLabel(line) {
  const p = line?.product || {};
  const name = String(p.cableName || '').toUpperCase();
  const n = Number(p.numberOfConductors || 0);
  const s = Number(p.section || 0);

  if (!name) return String(line?.productKey || '—');

  if (p.hasMixedConductorSections) {
    const gy = Number(p.greenYellowSection || 0);
    return `${name} ${n}×${s} + GY ${gy}${p.color ? ` (${p.color})` : ''}`;
  }

  return `${name} ${n}×${s}${p.color ? ` (${p.color})` : ''}`;
}

function lineState(line) {
  const total = num(line?.totalOrderedLength);
  const allocated = num(line?.allocatedLength);
  const produced = num(line?.producedLength);
  const stock = num(line?.stockReservedLength);

  // a line is fulfilled when production + reserved-from-stock covers the order
  const fulfilled = produced + stock;

  // fully covered by stock alone → distinct "stock" state (green, but labelled)
  if (total > 0 && fulfilled >= total)
    return produced <= 0 && stock > 0 ? 'stock' : 'done';

  // partial (produced some, reserved some from stock, or allocated to a job)
  if (fulfilled > 0 || allocated > 0) return 'partial';

  // not started
  return 'pending';
}

function lineBorderClass(state) {
  // border-2 → a bit wider for visibility (user request)
  if (state === 'done') return 'border-success border-2';
  if (state === 'stock') return 'border-success border-2';
  if (state === 'partial') return 'border-warning border-2';
  return 'border-danger border-2';
}

function getOrderAgg(order) {
  const lines = safeArr(order?.lines);

  const ordered = lines.reduce((s, l) => s + num(l.totalOrderedLength), 0);
  const allocated = lines.reduce((s, l) => s + num(l.allocatedLength), 0);
  const produced = lines.reduce((s, l) => s + num(l.producedLength), 0);
  const stock = lines.reduce((s, l) => s + num(l.stockReservedLength), 0);
  const jobs = lines.reduce(
    (s, l) => s + safeArr(l?.allocation?.allocations).length,
    0
  );

  // reserved-from-stock counts as fulfilled just like produced meters
  const fulfilled = Math.min(ordered, produced + stock);
  // committed = allocated to a job + reserved from stock
  const committed = allocated + stock;
  // remaining = what still has to be produced (stock already covers its share)
  const remaining = Math.max(0, ordered - produced - stock);

  const progress =
    ordered > 0 ? Math.min(100, Math.round((fulfilled / ordered) * 100)) : 0;

  // production badge logic (stock reservation is a first-class fulfilment path)
  // The chain runs success → info → primary → warning; nothing allocated is the
  // rung below all of them, not a neutral state. `getAllocBadge` in the order
  // details table already calls it danger, so this matches rather than invents.
  let prodBadge = { label: 'Not allocated', color: 'danger' };
  if (fulfilled >= ordered && ordered > 0) {
    prodBadge =
      produced > 0
        ? { label: 'Completed', color: 'success' }
        : { label: 'From stock', color: 'success' };
  } else if (produced > 0) {
    prodBadge = { label: 'In progress', color: 'info' };
  } else if (committed >= ordered && ordered > 0) {
    prodBadge = { label: 'Fully allocated', color: 'success' };
  } else if (stock > 0) {
    prodBadge = { label: 'Partly from stock', color: 'primary' };
  } else if (allocated > 0) {
    prodBadge = { label: 'Partially allocated', color: 'warning' };
  }

  return {
    ordered,
    allocated,
    produced,
    stock,
    committed,
    fulfilled,
    remaining,
    jobs,
    progress,
    prodBadge
  };
}

// aggregate the (optional, informative) job reservations for an order from its
// delivery items — reserved (launchedQty) vs produced (tokenIds) vs needed.
function getOrderReservationAgg(order) {
  let needed = 0;
  let reserved = 0;
  let produced = 0;
  for (const l of safeArr(order?.lines)) {
    for (const it of safeArr(l?.deliveryPlan?.items)) {
      const prod = Array.isArray(it.tokenIds)
        ? it.tokenIds.length
        : num(it.qtyProduced);
      needed += num(it.qtyPlanned);
      produced += prod;
      reserved += Math.max(num(it.launchedQty), prod);
    }
  }
  return { needed, reserved, produced };
}

function orderMatchesSearch(order, q) {
  if (!q) return true;
  const s = q.toLowerCase().trim();
  if (!s) return true;

  const lines = safeArr(order?.lines);

  const hay = [
    order?.orderCode,
    order?.orderId,
    order?._id,
    order?.client,
    order?.status,
    order?.dueDate,
    ...lines.map(l => l?.productKey),
    ...lines.map(l => l?.lineId),
    ...lines.map(l => l?.product?.cableName),
    ...lines.map(l => String(l?.product?.color || ''))
  ]
    .filter(Boolean)
    .map(x => String(x).toLowerCase())
    .join(' | ');

  return hay.includes(s);
}

function getOrderTabKey(order) {
  const o = getOrderAgg(order);
  const due = normalizeDateKey(order?.dueDate);
  const today = normalizeDateKey(new Date().toISOString());

  if (due && today && due < today && o.remaining > 0) return 'overdue';
  if (o.remaining <= 0 && o.ordered > 0) return 'done';
  if (o.produced > 0 || o.stock > 0) return 'in_progress';
  return 'pending';
}

function getProductionFilterKey(order) {
  const o = getOrderAgg(order);
  if (o.produced > 0) return 'in_progress';
  if (o.committed >= o.ordered && o.ordered > 0) return 'fully_allocated';
  if (o.committed > 0) return 'partially_allocated';
  return 'not_allocated';
}

// per-line delivery summary, e.g. "1× drum wood Ø1000" / "120× coil @100m"
function lineDeliveryText(line) {
  const items = safeArr(line?.deliveryPlan?.items);
  if (!items.length) return '';
  const parts = [];
  for (const it of items) {
    const kind = String(it.kind || '').toUpperCase();
    const qty = num(it.qtyPlanned);
    const len = num(it.lengthPerUnit);
    if (kind === 'DRUM') {
      const mat = String(it.drumMaterial || '').trim();
      parts.push(
        `${qty}× drum${mat ? ` ${mat}` : ''}${len ? ` @${fmt(len)}m` : ''}`
      );
    } else if (kind === 'COIL') {
      parts.push(`${qty}× coil${len ? ` @${fmt(len)}m` : ''}`);
    }
  }
  return parts.join(' · ');
}

// --- small UI: chips list for products in an order
function OrderLinesChips({ lines }) {
  const list = safeArr(lines);

  return (
    <div className="d-flex flex-wrap gap-2 mt-2">
      {list.slice(0, 6).map(l => {
        const label = buildLineLabel(l);
        const state = lineState(l);

        const total = num(l.totalOrderedLength);
        const produced = num(l.producedLength);
        const allocated = num(l.allocatedLength);
        const fromStock = num(l.stockReservedLength);
        // segments (fulfilment view): produced + from-stock + allocated-not-yet-produced
        const allocNotProd = Math.max(0, allocated - produced);
        const remaining = Math.max(
          0,
          total - produced - fromStock - allocNotProd
        );
        const seg = v => (total > 0 ? (v / total) * 100 : 0);
        const delivery = lineDeliveryText(l);

        return (
          <div
            key={l.lineId || label}
            className={`p-2 rounded-2 border ${lineBorderClass(state)} bg-body-emphasis d-flex flex-column`}
            style={{ fontSize: 11.5, minWidth: 178, maxWidth: 216 }}
            title={`${label} | produced ${fmt(produced)} / allocated ${fmt(
              allocated
            )} / ${fmt(total)} m${
              fromStock ? ` | ${fmt(fromStock)} m from stock` : ''
            }${delivery ? ' | ' + delivery : ''}`}
          >
            <div className="d-flex justify-content-between align-items-center gap-2 mb-1">
              <span className="fw-semibold text-truncate">{label}</span>
              <span className="text-muted fs-10 text-nowrap">
                {fmt(total)} m
              </span>
            </div>

            {/* stacked mini bar: produced / from-stock / allocated / remaining */}
            <div
              className="d-flex rounded-pill overflow-hidden mb-1"
              style={{ height: 6, background: 'rgba(128,128,128,0.25)' }}
            >
              <div
                style={{ width: `${seg(produced)}%`, background: '#2f9e44' }}
              />
              <div
                style={{ width: `${seg(fromStock)}%`, background: '#12b886' }}
              />
              <div
                style={{ width: `${seg(allocNotProd)}%`, background: '#3874ff' }}
              />
            </div>

            <div className="d-flex flex-wrap gap-1 fs-10">
              <span className="text-success fw-semibold">
                ● Prod {fmt(produced)} m
              </span>
              <span className="text-primary fw-semibold">
                ● Alloc {fmt(allocated)} m
              </span>
              {fromStock > 0 && (
                <span style={{ color: '#12b886' }} className="fw-semibold">
                  ● Stock {fmt(fromStock)} m
                </span>
              )}
              {remaining > 0 && (
                <span className="text-muted">○ Rem {fmt(remaining)} m</span>
              )}
            </div>

            {delivery && (
              <span className="text-muted fs-10 mt-1">{delivery}</span>
            )}
          </div>
        );
      })}

      {list.length > 6 && (
        <span
          className="px-2 py-1 rounded-2 border border-translucent text-muted align-self-center"
          style={{ fontSize: 11.5 }}
        >
          +{list.length - 6} more
        </span>
      )}
    </div>
  );
}

// `fullPage` = rendered on its own dedicated route rather than under the KPI
// strip and charts on Order Monitor, so it gets a taller page of orders.
const OrdersMonitorTable = ({ orders = [], onRefresh, fullPage = false }) => {
  // top controls
  const [searchTerm, setSearchTerm] = useState('');
  const [tabKey, setTabKey] = useState('all'); // all | pending | in_progress | done | overdue
  const [prodFilter, setProdFilter] = useState('all'); // all | not_allocated | partially_allocated | fully_allocated | in_progress

  // reserve-from-stock: open the modal for an EXISTING order on demand
  const [reserveOrder, setReserveOrder] = useState(null);

  const handleSearchInputChange = e => setSearchTerm(e.target.value || '');

  const tabItems = useMemo(() => {
    const list = orders || [];
    const counts = {
      all: list.length,
      pending: 0,
      in_progress: 0,
      done: 0,
      overdue: 0
    };

    for (const o of list) {
      const k = getOrderTabKey(o);
      counts[k] = (counts[k] || 0) + 1;
    }

    return [
      { label: 'All', value: 'all', count: counts.all },
      { label: 'Pending', value: 'pending', count: counts.pending },
      { label: 'In progress', value: 'in_progress', count: counts.in_progress },
      { label: 'Done', value: 'done', count: counts.done },
      { label: 'Overdue', value: 'overdue', count: counts.overdue }
    ];
  }, [orders]);

  const filterMenus = useMemo(() => {
    return [
      {
        label: 'Production',
        items: [
          { label: 'All', onClick: () => setProdFilter('all') },
          {
            label: 'Not allocated',
            onClick: () => setProdFilter('not_allocated')
          },
          {
            label: 'Partially allocated',
            onClick: () => setProdFilter('partially_allocated')
          },
          {
            label: 'Fully allocated',
            onClick: () => setProdFilter('fully_allocated')
          },
          { label: 'In progress', onClick: () => setProdFilter('in_progress') }
        ]
      }
    ];
  }, []);

  const filteredOrders = useMemo(() => {
    return (orders || []).filter(o => {
      // tab
      if (tabKey !== 'all' && getOrderTabKey(o) !== tabKey) return false;

      // production filter
      if (prodFilter !== 'all' && getProductionFilterKey(o) !== prodFilter)
        return false;

      // search
      if (!orderMatchesSearch(o, searchTerm)) return false;

      return true;
    });
  }, [orders, tabKey, prodFilter, searchTerm]);

  const columns = useMemo(
    () => [
      {
        accessorKey: 'orderCode',
        header: 'Order',
        cell: ({ row: { original } }) => {
          const id = original.orderId || original._id;
          const a = getOrderAgg(original);

          return (
            <div className="d-flex flex-column">
              <div className="d-flex align-items-center justify-content-between gap-3">
                <div className="d-flex flex-column">
                  <Link
                    to={`/apps/sales-management/order-details/${id}`}
                    className="fw-bold text-decoration-none"
                    title={String(id || '')}
                  >
                    {original.orderCode || String(id || '').slice(0, 8) + '…'}
                  </Link>
                  <Badge
                    variant="phoenix"
                    bg="primary"
                    className="fs-10 align-self-start mt-1"
                  >
                    {String(original.client || '—').toUpperCase()}
                  </Badge>
                </div>

                <div className="d-flex flex-column align-items-end">
                  <span className="text-muted fs-10">Jobs</span>
                  <span className="fw-semibold">{fmt(a.jobs)}</span>
                </div>
              </div>

              {/* ✅ product chips */}
              <OrderLinesChips lines={original.lines} />

              <div className="mt-2 d-flex flex-wrap align-items-center gap-2">
                {/* reserve-from-stock: available while there's still meters to
                    produce — lets the agent cover it from stock instead */}
                {a.remaining > 0 && (
                  <Button
                    size="sm"
                    variant="outline-success"
                    className="py-0 px-2 fs-10"
                    onClick={() => setReserveOrder(original)}
                  >
                    <FontAwesomeIcon icon={faWarehouse} className="me-1" />
                    Reserve from stock
                  </Button>
                )}

                {/* job reservations for this order (drums/coils reserved at launch) —
                    visible marker + link to the delivery & reservations board */}
                {(() => {
                  const r = getOrderReservationAgg(original);
                  if (r.needed <= 0) return null;
                  return (
                    <Link
                      to={`/apps/sales-management/reservations/${id}`}
                      className="text-decoration-none"
                      title="Delivery & reservations for this order"
                    >
                      {/* A link is an invitation, not a warning: info keeps
                          "nothing reserved yet" reading as something you can go
                          and do, rather than a dead grey chip. */}
                      <Badge
                        variant="phoenix"
                        bg={r.reserved > 0 ? 'primary' : 'info'}
                        className="fs-10"
                      >
                        🎗{' '}
                        {r.reserved > 0
                          ? `${fmt(r.reserved)} reserved`
                          : 'View reservations'}
                      </Badge>
                    </Link>
                  );
                })()}
              </div>
            </div>
          );
        },
        meta: { headerProps: { style: { minWidth: 520 } } }
      },
      {
        accessorKey: 'dueDate',
        header: 'Due',
        cell: ({ row: { original } }) => {
          const due = normalizeDateKey(original?.dueDate);
          const today = normalizeDateKey(new Date().toISOString());
          const isOverdue =
            due && today && due < today && getOrderAgg(original).remaining > 0;

          return (
            <span className={isOverdue ? 'text-danger fw-semibold' : ''}>
              {formatDate(original.dueDate)}
            </span>
          );
        },
        meta: {
          headerProps: { style: { width: 140 }, className: 'text-center' },
          cellProps: { className: 'text-center' }
        }
      },
      {
        id: 'prodStatus',
        header: 'Production',
        accessorFn: row => getOrderAgg(row).prodBadge.label,
        cell: ({ row: { original } }) => {
          const { prodBadge } = getOrderAgg(original);
          return (
            <Badge
              bg={prodBadge.color}
              className="fs-10 rounded-pill w-100 py-2 text-center"
            >
              {prodBadge.label}
            </Badge>
          );
        },
        meta: {
          headerProps: { style: { width: 170 }, className: 'text-center' },
          cellProps: { className: 'text-center' }
        }
      },
      {
        id: 'meters',
        header: 'Meters',
        cell: ({ row: { original } }) => {
          const a = getOrderAgg(original);
          return (
            <div className="d-flex flex-column gap-1">
              <div className="d-flex justify-content-between">
                <span className="text-muted fs-10">ordered</span>
                <span className="fw-semi-bold">{fmt(a.ordered)} m</span>
              </div>
              <div className="d-flex justify-content-between">
                <span className="text-muted fs-10">allocated</span>
                <span className="fw-semi-bold">{fmt(a.allocated)} m</span>
              </div>
              {a.stock > 0 && (
                <div className="d-flex justify-content-between">
                  <span className="text-success fs-10">from stock</span>
                  <span className="fw-semi-bold text-success">
                    {fmt(a.stock)} m
                  </span>
                </div>
              )}
              <div className="d-flex justify-content-between">
                <span className="text-muted fs-10">produced</span>
                <span className="fw-semi-bold">{fmt(a.produced)} m</span>
              </div>
              <div className="d-flex justify-content-between">
                <span className="text-muted fs-10">remaining</span>
                <span className="fw-semi-bold">{fmt(a.remaining)} m</span>
              </div>
            </div>
          );
        },
        meta: { headerProps: { style: { width: 240 } } }
      },
      {
        id: 'progress',
        header: 'Progress',
        cell: ({ row: { original } }) => {
          const a = getOrderAgg(original);
          const variant =
            a.progress < 20
              ? 'danger'
              : a.progress < 60
                ? 'warning'
                : a.progress < 90
                  ? 'info'
                  : 'success';

          return (
            <div className="d-flex flex-column align-items-center">
              {/* At 0% the bar has no width, so its own label is invisible and
                  all that shows is the empty track — a grey smear that reads as
                  a broken widget rather than a number. The percentage is
                  printed beside it in the variant's own colour, so nought is as
                  legible as ninety. */}
              <div className="d-flex align-items-center gap-2 w-100 justify-content-center">
                <ProgressBar
                  now={a.progress}
                  variant={variant}
                  style={{ height: 14, width: '70%' }}
                />
                <span className={`fs-10 fw-bold text-${variant}`}>
                  {a.progress}%
                </span>
              </div>
              <span className="text-muted fs-10 mt-1">
                {a.stock > 0 ? (
                  <>
                    fulfilled {fmt(a.fulfilled)} / {fmt(a.ordered)} m
                    <span className="text-success">
                      {' '}
                      ({fmt(a.stock)} m stock)
                    </span>
                  </>
                ) : (
                  <>
                    produced {fmt(a.produced)} / {fmt(a.ordered)} m
                  </>
                )}
              </span>
            </div>
          );
        },
        meta: {
          headerProps: { style: { width: 260 }, className: 'text-center' },
          cellProps: { className: 'text-center' }
        }
      }
    ],
    []
  );

  const table = useAdvanceTable({
    data: filteredOrders,
    columns,
    pageSize: fullPage ? FULL_TABLE_PAGE_SIZE.ordersMonitor : 8,
    pagination: true,
    sortable: true
  });

  return (
    <div className="mb-6">
      <div className="d-flex align-items-center gap-3 mb-3">
        <h3 className="mb-0">Orders Monitor</h3>
        {!fullPage && (
          <TableExpandButton
            to={FULL_TABLE_ROUTES.ordersMonitor}
            className="ms-auto"
          />
        )}
      </div>

      {/* ✅ Top section (like Bobin State) */}
      <FilterTab
        tabItems={tabItems}
        activeKey={tabKey}
        onSelect={setTabKey}
        className="mb-3"
      />

      <AdvanceTableProvider {...table}>
        <div className="mb-4">
          <div className="d-flex flex-wrap gap-3 align-items-center">
            <SearchBox
              placeholder="Search client / product / order..."
              onChange={handleSearchInputChange}
            />

            <div className="scrollbar overflow-hidden-y">
              <FilterButtonGroup menus={filterMenus} />
            </div>

            <div className="ms-auto text-muted fs-10">
              Showing <b>{filteredOrders.length}</b> / {orders.length}
            </div>
          </div>
        </div>

        <div className="border-top border-200">
          <AdvanceTable tableProps={{ className: 'phoenix-table fs-9' }} />
          <AdvanceTableFooter pagination className="py-3" />
        </div>
      </AdvanceTableProvider>

      <ReserveFromStockModal
        order={reserveOrder}
        show={!!reserveOrder}
        onHide={changed => {
          setReserveOrder(null);
          if (changed && typeof onRefresh === 'function') onRefresh();
        }}
      />
    </div>
  );
};

export default OrdersMonitorTable;
